import React, { useState } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Axios from "axios";
import useUserState from "../../hooks/useUserState";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import consultationIcon from "../../../images/consultation-icon.svg";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "600px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  icon: {
    width: "60px",
    height: "60px",
    marginRight: theme.spacing(2),
  },
  formContainer: {
    padding: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  loadingButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const DentalChartModal = ({ open, handleClose, patientId, onSave }) => {
  const classes = useStyles();
  const [chartType, setChartType] = useState("adult");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const userLocalState = useUserState();
  const { t } = useBDocTranslator();

  const handleSave = async () => {
    if (!patientId) {
      setError(t("Patient ID is required"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr`,
        {
          patient_id: patientId,
          provider_id: userLocalState[0]?.user.id,
          chart_type: chartType,
          teeth_data: {},
          notes: notes.trim(),
        },
        {
          headers: { Authorization: "Bearer " + userLocalState[0]?.jwt },
        }
      );

      onSave?.(response.data);
      handleCloseModal();
    } catch (error) {
      console.error("Error creating dental chart:", error);
      if (error.response?.status === 409) {
        setError(t("A dental chart already exists for this patient"));
      } else if (error.response?.status === 403) {
        setError(t("You don't have permission to create dental charts"));
      } else {
        setError(t("Error creating dental chart. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setNotes("");
    setChartType("adult");
    setError("");
    setLoading(false);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <img
              src={consultationIcon}
              alt="dental chart icon"
              className={classes.icon}
            />
            <div>
              <Typography variant="h6">
                <b>{t("New")}</b> - {t("Dental Chart")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("Create a new dental chart for the patient")}
              </Typography>
            </div>
          </div>

          <div className={classes.formContainer}>
            {error && (
              <Typography
                variant="body2"
                color="error"
                style={{ marginBottom: "16px" }}
              >
                {error}
              </Typography>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{t("Chart Type")}</InputLabel>
                  <Select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    label={t("Chart Type")}
                    disabled={loading}
                  >
                    <MenuItem value="adult">{t("Adult")}</MenuItem>
                    <MenuItem value="child">{t("Child")}</MenuItem>
                    <MenuItem value="mixed">{t("Mixed")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("Initial Notes")}
                  multiline
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("Enter initial observations or notes")}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <div className={classes.buttonContainer}>
                  <Button
                    onClick={handleCloseModal}
                    disabled={loading}
                    size="large"
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                    size="large"
                    className={classes.loadingButton}
                  >
                    {loading && <CircularProgress size={20} />}
                    {loading ? t("Creating...") : t("Create Chart")}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default DentalChartModal;
